import { Center, Group, Loader, Stack, Text } from '@mantine/core';
import {
  useSuspenseQuery,
  useQuery,
  keepPreviousData,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ExtendedCombobox from '../../../../components/extended-combobox/extended-combobox';
import { FieldMapsLayout } from '../../../../components/field-maps-layout/field-maps-layout';
import SelectMapType from '../../../../components/select-map-type/select-map-type';
import useSharedFieldMaps from '../../../../hooks/use-shared-field-maps/shared-field-maps';
import { MAP_DISPLAY_TYPES } from '../compare-maps/page';

import { FORM_STATUS } from '@advices/data-access';
import { useAuth } from '@auth/data-access';
import { hasAdminAccess, PRESCRIPTION_FROM_TYPES } from '@data-access';
import {
  formatBiomassLegend,
  LegendControl,
  PfMap,
  ZoningLayer,
  ZoningLegend,
} from '@map';
import { TsrButtonLink } from '@ui';

interface BiomassMapProps {
  type: MAP_DISPLAY_TYPES;
}

export function BiomassMap({ type }: BiomassMapProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    field,
    fieldId,
    mapId,
    compare,
    queries,
    handleCompareChange,
    navigateMapIdSp,
  } = useSharedFieldMaps({ type });

  const summary = useSuspenseQuery(queries.biomassSummary(Number(fieldId)));
  const [selectedMap, setSelectedMap] = useState(
    mapId?.toString() ?? summary.data[0]?.id.toString() ?? null,
  );

  const map = useQuery({
    ...queries.biomassMap(Number(selectedMap)),
    enabled: selectedMap !== null,
    placeholderData: keepPreviousData,
    refetchInterval(query) {
      return query.state.data?.status !== FORM_STATUS.FINISHED ? 5000 : false;
    },
  });

  const options = summary.data.map((item) => ({
    value: item.id.toString(),
    label: item.name,
    description: [
      `${t('common.created_date_prefix')} ${dayjs(item.created_at).format(
        `DD/MM/YYYY [${t('common.time_prefix')}] H[h]mm`,
      )}`,
    ],
  }));

  useEffect(() => {
    if (mapId === undefined && selectedMap !== null) {
      navigateMapIdSp(selectedMap);
    }
  }, [mapId, navigateMapIdSp, selectedMap]);

  return (
    <FieldMapsLayout
      content={
        <>
          {map.data?.status === FORM_STATUS.FINISHED ? (
            <PfMap zoomToGeojson={field.data}>
              <ZoningLayer data={map.data.map} filled outline={false} />
              <LegendControl>
                <ZoningLegend
                  {...formatBiomassLegend(map.data?.map)}
                  mean={map.data.mean}
                />
              </LegendControl>
            </PfMap>
          ) : null}

          {map.data?.status === FORM_STATUS.PENDING ? (
            <Center h="100%" p="xl">
              <Stack align="center">
                <Text fw="bold" size="xl" ta="center">
                  {t('common.preparing-map')}
                </Text>
                <Loader />
              </Stack>
            </Center>
          ) : null}

          {summary.data?.length === 0 ? (
            <Center h="100%" p="xl">
              <Stack>
                <Text fw="bold" size="xl" ta="center">
                  {t('common.no-biomass-map-available')}
                </Text>
              </Stack>
            </Center>
          ) : null}
        </>
      }
      header={
        <Group justify="space-between" p="md">
          <Group>
            {type !== 'single' ? (
              <SelectMapType
                maps={field.data.properties.maps}
                onChange={handleCompareChange}
                value={type === 'compare-left' ? compare?.[0] : compare?.[1]}
              />
            ) : null}
            {summary.data.length ? (
              <ExtendedCombobox
                isPending={summary.isPending || map.isFetching}
                onOptionSubmit={(val) => {
                  navigateMapIdSp(val);
                  setSelectedMap(val);
                }}
                options={options}
                selectedOption={selectedMap}
              />
            ) : null}
          </Group>
          <Group>
            {hasAdminAccess(user?.user_access) ? (
              <TsrButtonLink
                color="dark"
                params={{
                  fieldId,
                }}
                to="/fields/$fieldId/maps/biomass/settings"
              >
                {t('advices.settings')}
              </TsrButtonLink>
            ) : null}
            <TsrButtonLink
              disabled={selectedMap === null}
              params={{
                fieldId,
              }}
              search={
                selectedMap !== null
                  ? {
                      mapType: PRESCRIPTION_FROM_TYPES.BIOMASS,
                      mapId: Number(selectedMap),
                    }
                  : {}
              }
              to="/fields/$fieldId/maps/prescriptions/create"
            >
              {t('prescriptions.createPrescriptionTitle')}
            </TsrButtonLink>
          </Group>
        </Group>
      }
    />
  );
}

export default BiomassMap;
